<div class="form-builder">
  <!-- Loading Overlay -->
  @if (loadingForm()) {
    <div class="loading-overlay">
      <div class="loading-spinner">
        <i class="pi pi-spin pi-spinner"></i>
        <span class="loading-text">Loading form...</span>
        <span class="loading-subtext">Please wait while we load your form</span>
      </div>
    </div>
  }
  <main class="form-builder-content" [class.loading]="loadingForm()">
    <!-- Toolbar Component -->
    <app-form-toolbar
      [showQuestionNumbers]="showQuestionNumbers()"
      [formStatus]="formStatus()"
      [isReadOnly]="isReadOnly()"
      [hasFormElements]="hasFormElements()"
      [hasUnsavedChanges]="unsavedChanges()"
      [promotion]="promotion()"
      [promotionTarget]="promotionTarget"
      (showQuestionNumbersChange)="onShowQuestionNumbersChange($event)"
      (clearForm)="showClearConfirmation.set(true)"
      (openSavedForms)="openFormSelector()"
      (compare)="openCompareSelector()"
      [(typeMenuOpen)]="showFormTypeSelector"
      (createForm)="onCreateFormClick($event)"
      (saveDraft)="saveDraft()"
      (publish)="showPublishConfirmation.set(true)"
      (promote)="promoteToProduction()"
      (approvePromotion)="showApprovePromotionConfirmation.set(true)"
      (archive)="onArchiveForm()">
    </app-form-toolbar>

    <!-- Header Component -->
    <app-form-header
      [formTitle]="formTitle()"
      [formDescription]="formDescription()"
      [formVersion]="formVersion()"
      [formType]="formType()"
      [formStatus]="formStatus()"
      [promotion]="promotion()"
      [promotionTarget]="promotionTarget"
      [isReadOnly]="isReadOnly()"
      (titleChange)="onFormTitleChange($event)"
      (descriptionChange)="onFormDescriptionChange($event)"
      (versionChange)="onFormVersionChange($event)">
      <!-- Application templates only; delete this block and the component's
           folder to remove assessment matrices entirely. -->
      @if (formType() === 'Application') {
        <app-assessment-matrices
          formHeaderExtras
          [(matrixIds)]="assessmentMatrixIds"
          [isReadOnly]="isReadOnly()" />
      }
    </app-form-header>

    <!-- The questions, split with a saved form shown read-only beside them
         while comparing. The toolbar and form header above stay full width. -->
    <div class="form-workspace" [class.comparing]="!!compareTemplate()">
    @if (compareTemplate(); as template) {
      <!-- Full width, so it names the compared form over both columns at once. -->
      <header class="compare-bar">
        <div class="compare-heading">
          <span class="compare-label">Comparing with</span>
          <h2 class="compare-title" [title]="template.displayName">
            {{ template.name || template.displayName }}
          </h2>
          <span class="compare-meta">Version {{ template.version }}</span>
          <span class="compare-status"
            [class.draft]="template.status === 'Draft'"
            [class.published]="template.status === 'Published'"
            [class.archived]="template.status === 'Archived'">
            {{ template.status }}
          </span>
        </div>
        <div class="compare-actions">
          <button type="button" class="compare-change" (click)="openCompareSelector()">
            <i class="pi pi-folder-open"></i>
            <span>Change form</span>
          </button>
          <button type="button" class="compare-close" (click)="closeCompare()"
            aria-label="Close comparison" title="Close comparison">
            <i class="pi pi-times"></i>
          </button>
        </div>
      </header>
      <!-- Which column is which, since the bar above spans them both. -->
      <div class="compare-captions">
        <span class="compare-caption">This form</span>
        <span class="compare-caption compare-caption-other">Comparing with</span>
      </div>
    }
    <div class="workspace-columns">
    <div class="questions-scrollable-area">
      @if (questions().length === 0) {
        <div class="empty-state-menu">
          <app-add-question-button (add)="handleAddNew()" [disabled]="isReadOnly()"></app-add-question-button>
        </div>
      }
      <div class="questions-container">
      @for (question of questions(); track question.id; let i = $index; let last = $last) {
        <div
          class="question-row"
          [attr.data-question-id]="question.id"
          [class.drop-before]="dropGapIndex() === i"
          [class.drop-after]="last && dropGapIndex() === questions().length"
          [class.dragging]="draggedQuestionId() === question.id"
          [class.editing]="question.isEditing ?? true"
          (mouseenter)="onQuestionRowMouseEnter(question.id, last)"
          (mouseleave)="onQuestionRowMouseLeave(question.id, $event)"
          (dragover)="onDragOver($event, i)"
          (dragleave)="onDragLeave()"
          (drop)="onDrop($event, i)">
          <div class="question-row-content"
               (dragover)="onDragOver($event, i); $event.stopPropagation()"
               (drop)="onDrop($event, i); $event.stopPropagation()">
            <div class="question-menu"
                 [class.visible]="isQuestionMenuVisible(question, last)"
                 (mouseenter)="onQuestionRowMouseEnter(question.id, last)"
                 (mouseleave)="onQuestionRowMouseLeave(question.id, $event)"
                 (mouseover)="onQuestionRowMouseEnter(question.id, last)">
              <app-form-context-menu
                [questionId]="question.id"
                [isEditing]="question.isEditing ?? true"
                [hasQuestionType]="!!question.questionType"
                (sort)="handleSort()"
                (delete)="handleDelete(question.id)"
                (insertBelow)="handleInsertBelow(question.id)"
                (dragStart)="onDragStart($event, question.id)"
                (dragEnd)="onDragEnd()"
                (accept)="acceptQuestion(question.id)"
                (edit)="editQuestion(question.id)">
              </app-form-context-menu>
            </div>
            <div class="question-content">
              <!-- Required toggle asterisk - shown in preview mode for non-structural questions -->
              @if (!(question.isEditing ?? true) && question.questionType && question.questionType !== QuestionType.Section && question.questionType !== QuestionType.Title && question.questionType !== QuestionType.HelperText) {
                <div class="required-toggle-wrapper">
                  <button
                    type="button"
                    class="required-toggle"
                    [class.required]="question.isRequired"
                    (click)="toggleRequired(question.id)"
                    [disabled]="isReadOnly()"
                    [attr.aria-label]="question.isRequired ? 'Mark as optional' : 'Mark as required'">
                    <i class="pi pi-asterisk"></i>
                  </button>
                  <span class="required-tooltip">{{ question.isRequired ? 'Required - click to make optional' : 'Optional - click to make required' }}</span>
                </div>
              }
              @if (showQuestionNumbers() && question.questionType !== QuestionType.Section && question.questionType !== QuestionType.Title && question.questionType !== QuestionType.HelperText) {
                <span class="question-number">{{ question.questionNumber || (i + 1) }}.</span>
              }
              @if (question.questionType) {
                <div class="question-component-wrapper">
                  <app-question-container
                    [question]="question"
                    [isEditing]="!isReadOnly() && (question.isEditing ?? true)"
                    [disabled]="isReadOnly()"
                    (questionChange)="onQuestionChange($event)"
                    (configChange)="onQuestionConfigChange(question.id, $event)"
                    (delete)="handleDelete(question.id)">
                  </app-question-container>
                </div>
              } @else {
                <input
                  type="text"
                  class="question-label-input"
                  placeholder="Select a question type from the palette"
                  [(ngModel)]="question.labelText"
                  [attr.data-question-input-id]="question.id"
                  readonly>
              }
            </div>
          </div>
        </div>
      }
      @if (questions().length > 0) {
        <div class="add-new-row-menu">
          <app-add-question-button (add)="handleAddNew()" [disabled]="isReadOnly()"></app-add-question-button>
        </div>
      }
      </div>
    </div>
    @if (compareTemplate(); as template) {
      <app-form-compare-pane
        [template]="template"
        [showQuestionNumbers]="showQuestionNumbers()" />
    }
    </div>
    </div>
  </main>
  <app-form-element-palette
    [(visible)]="showPalette"
    (elementSelected)="onElementSelected($event)"
    (cancelled)="onPaletteCancelled()">
  </app-form-element-palette>
  <app-form-selector-modal
    [(visible)]="showFormSelector"
    [mode]="formSelectorMode()"
    (formSelected)="onFormSelected($event)"
    (formCompared)="compareTemplate.set($event)"
    (formCloned)="onFormCloned($event)">
  </app-form-selector-modal>
  <app-confirmation-dialog
    [(visible)]="showClearConfirmation"
    title="Clear Form"
    message="Are you sure you want to clear the form? This will remove all questions for this session and cannot be undone."
    confirmText="Clear"
    cancelText="Cancel"
    (confirmed)="onClearForm()">
  </app-confirmation-dialog>
  <app-confirmation-dialog
    [(visible)]="showPublishConfirmation"
    title="Publish Form"
    message="Are you sure you want to publish this form? Once published, the form will be available for use. This action should be performed with caution."
    confirmText="Publish"
    cancelText="Cancel"
    (confirmed)="publishForm()">
  </app-confirmation-dialog>
  <app-confirmation-dialog
    [(visible)]="showApprovePromotionConfirmation"
    title="Approve Promotion"
    message="Confirm the client has approved this version. It will then be copied to production as it stands."
    confirmText="Mark approved"
    cancelText="Cancel"
    (confirmed)="approvePromotion()">
  </app-confirmation-dialog>
  <app-confirmation-dialog
    [(visible)]="showArchiveConfirmation"
    title="Archive Form"
    message="Are you sure you want to archive this form? Archived forms are no longer available for use but can be restored later."
    confirmText="Archive"
    cancelText="Cancel"
    (confirmed)="archiveForm()">
  </app-confirmation-dialog>

  <app-unsaved-changes-dialog
    [(visible)]="showUnsavedChangesDialog"
    (save)="onUnsavedChangesSave()"
    (dontSave)="onUnsavedChangesDontSave()"
    (cancel)="onUnsavedChangesCancel()">
  </app-unsaved-changes-dialog>


  <p-toast></p-toast>
</div>

